/**
 * Ce que les écrans des centres lisent et écrivent.
 *
 * Sous les droits de l'administration (RLS de `centers`, `centre_classes`) ;
 * la suppression d'une classe passe par le geste `supprimer_classe`, qui
 * déplace ses élèves et ses journées en une transaction.
 *
 * Les colonnes des journées pédagogiques (`centre_de_vacances`, `school_id`)
 * n'existent qu'après leur migration : on lit `*` et on regarde ce qui est
 * là, pour qu'un écran déployé avant elle gère encore les centres.
 */
import { db } from '../payments/db';
import { lireClasses } from '../jpa/gateway.supabase';
import type { ClasseDuCentre } from '../jpa/regles';
import { ligneDuCentre, type Centre, type SaisieDeCentre, type StatutDeCentre } from './regles';

interface CentreLu {
  id: string;
  name: string;
  tag: string;
  address: string | null;
  address2: string | null;
  postal_code: string | null;
  city: string | null;
  phone: string | null;
  statut: StatutDeCentre | null;
  archived: boolean | null;
  archived_at: string | null;
  centre_de_vacances?: boolean;
  school_id?: string | null;
}

const versCentre = (l: CentreLu): Centre => ({
  id: l.id,
  nom: l.name,
  code: l.tag,
  adresse: l.address ?? '',
  adresse2: l.address2,
  codePostal: l.postal_code ?? '',
  localite: l.city ?? '',
  telephone: l.phone,
  statut: l.statut ?? 'actif',
  archive: l.archived === true,
  archiveLe: l.archived_at,
  centreDeVacances: typeof l.centre_de_vacances === 'boolean' ? l.centre_de_vacances : null,
  ecoleId: l.school_id ?? null,
});

/** Une table ou une colonne que l'API ne connaît pas encore. */
const absente = (e: { code?: string } | null) =>
  e?.code === 'PGRST205' || e?.code === 'PGRST204' || e?.code === '42703' || e?.code === '42P01';

export async function lireCentres(avecArchives: boolean): Promise<Centre[]> {
  let requete = db.from('centers').select('*').order('name');
  if (!avecArchives) requete = requete.eq('archived', false);
  const { data, error } = await requete;
  if (error) throw error;
  return ((data ?? []) as CentreLu[]).map(versCentre);
}

export async function lireCentre(id: string): Promise<Centre | null> {
  const { data, error } = await db.from('centers').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  return data ? versCentre(data as CentreLu) : null;
}

/** La base porte-t-elle les colonnes des journées pédagogiques ? */
export async function colonnesJpaPresentes(): Promise<boolean> {
  const { error } = await db.from('centers').select('centre_de_vacances').limit(1);
  if (!error) return true;
  if (absente(error)) return false;
  throw error;
}

export async function creerCentre(s: SaisieDeCentre, avecColonnesJpa: boolean): Promise<string> {
  const { data, error } = await db.from('centers')
    .insert({ ...ligneDuCentre(s, avecColonnesJpa), active: true, archived: false })
    .select('id')
    .single();
  if (error) throw error;
  return (data as { id: string }).id;
}

export async function modifierCentre(id: string, s: SaisieDeCentre, avecColonnesJpa: boolean): Promise<void> {
  const { error } = await db.from('centers').update(ligneDuCentre(s, avecColonnesJpa)).eq('id', id);
  if (error) throw error;
}

export async function archiverCentre(id: string, archive: boolean): Promise<void> {
  const { error } = await db.from('centers')
    .update({ archived: archive, archived_at: archive ? new Date().toISOString() : null })
    .eq('id', id);
  if (error) throw error;
}

export async function supprimerCentre(id: string): Promise<void> {
  const { error } = await db.from('centers').delete().eq('id', id);
  if (error) throw error;
}

export interface UsageDuCentre {
  /** Toutes les sessions, passées comprises : un centre qui en a ne se supprime pas. */
  sessions: number;
  /** Les stages à venir, non archivés : ils le gardent centre de vacances. */
  stagesAVenir: number;
  journeesPedagogiques: number;
}

export async function usageDuCentre(id: string, aujourdHui: string): Promise<UsageDuCentre> {
  const { data, error } = await db.from('sessions')
    .select('id, end_date, archived, stage:stages(type_activite)')
    .eq('center_id', id);
  if (error) throw error;
  const lignes = (data ?? []) as {
    end_date: string; archived: boolean | null;
    stage: { type_activite?: string | null } | { type_activite?: string | null }[] | null;
  }[];
  const type = (l: (typeof lignes)[number]) => (Array.isArray(l.stage) ? l.stage[0] : l.stage)?.type_activite ?? null;
  return {
    sessions: lignes.length,
    stagesAVenir: lignes.filter((l) => type(l) !== 'jpa' && l.end_date >= aujourdHui && l.archived !== true).length,
    journeesPedagogiques: lignes.filter((l) => type(l) === 'jpa').length,
  };
}

export interface Ecole {
  id: string;
  nom: string;
}

export async function lireEcoles(): Promise<Ecole[]> {
  const { data, error } = await db.from('schools').select('id, name').order('name');
  if (error) throw error;
  return ((data ?? []) as { id: string; name: string }[]).map((e) => ({ id: e.id, nom: e.name }));
}

/** Le nombre de classes de chaque centre ; vide si la table n'existe pas encore. */
export async function classesParCentre(centreIds: string[]): Promise<Map<string, number>> {
  const resultat = new Map<string, number>();
  if (centreIds.length === 0) return resultat;
  const { data, error } = await db.from('centre_classes').select('center_id').in('center_id', centreIds);
  if (absente(error)) return resultat;
  if (error) throw error;
  for (const l of (data ?? []) as { center_id: string }[]) {
    resultat.set(l.center_id, (resultat.get(l.center_id) ?? 0) + 1);
  }
  return resultat;
}

/** Les classes d'un centre, masquées comprises. */
export function classesDuCentre(centreId: string): Promise<ClasseDuCentre[]> {
  return lireClasses([centreId], true);
}

/** Combien de journées pédagogiques admettent chaque classe. */
export async function journeesParClasse(classeIds: string[]): Promise<Map<string, number>> {
  const resultat = new Map<string, number>();
  if (classeIds.length === 0) return resultat;
  const { data, error } = await db.from('jpa_classes_admises').select('classe_id').in('classe_id', classeIds);
  if (error) throw error;
  for (const l of (data ?? []) as { classe_id: string }[]) {
    resultat.set(l.classe_id, (resultat.get(l.classe_id) ?? 0) + 1);
  }
  return resultat;
}

/** Supprimer une classe ; ses élèves passent dans `vers`, ou n'ont plus de classe. */
export async function supprimerClasse(classeId: string, vers: string | null): Promise<{ eleves: number; journees: number }> {
  const { data, error } = await db.rpc('supprimer_classe', { p_classe_id: classeId, p_vers: vers });
  if (error) throw error;
  const r = (data ?? {}) as { enfants?: number; journees?: number };
  return { eleves: Number(r.enfants ?? 0), journees: Number(r.journees ?? 0) };
}
